export const AUTH_ATTEMPT_STORAGE_KEY = "home-twice:pending-email-link";
export const AUTH_ATTEMPT_TTL_MS = 60 * 60 * 1000;

export interface PendingAuthAttempt {
  id: string;
  returnPath: string;
  createdAt: number;
}

export type AuthAttemptStatus = "matched" | "missing" | "different-browser" | "expired";

type AttemptStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function getSafeStorage(): AttemptStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function createAuthAttemptId(cryptoApi: Pick<Crypto, "getRandomValues"> = crypto): string {
  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}

export function rememberAuthAttempt(attempt: PendingAuthAttempt): boolean;
export function rememberAuthAttempt(attempt: PendingAuthAttempt, storage: AttemptStorage): boolean;
export function rememberAuthAttempt(
  attempt: PendingAuthAttempt,
  storage?: AttemptStorage,
): boolean {
  try {
    const target = storage ?? getSafeStorage();
    if (!target) return false;
    target.setItem(AUTH_ATTEMPT_STORAGE_KEY, JSON.stringify(attempt));
    return true;
  } catch {
    return false;
  }
}

export function getAuthAttemptStatus(attemptId: string | null, now?: number): AuthAttemptStatus;
export function getAuthAttemptStatus(
  attemptId: string | null,
  storage: AttemptStorage,
  now?: number,
): AuthAttemptStatus;
export function getAuthAttemptStatus(
  attemptId: string | null,
  storageOrNow?: AttemptStorage | number,
  suppliedNow = Date.now(),
): AuthAttemptStatus {
  const storage = typeof storageOrNow === "object" ? storageOrNow : getSafeStorage();
  const now = typeof storageOrNow === "number" ? storageOrNow : suppliedNow;
  if (!attemptId || !storage) return "missing";

  try {
    const raw = storage.getItem(AUTH_ATTEMPT_STORAGE_KEY);
    if (!raw) return "missing";
    const stored = JSON.parse(raw) as Partial<PendingAuthAttempt>;
    if (stored.id !== attemptId) return "different-browser";
    if (typeof stored.createdAt !== "number" || now - stored.createdAt > AUTH_ATTEMPT_TTL_MS) {
      return "expired";
    }
    return "matched";
  } catch {
    return "missing";
  }
}

export function clearAuthAttempt(attemptId?: string | null): void;
export function clearAuthAttempt(attemptId: string | null | undefined, storage: AttemptStorage): void;
export function clearAuthAttempt(
  attemptId?: string | null,
  storage?: AttemptStorage,
): void {
  try {
    const target = storage ?? getSafeStorage();
    if (!target) return;
    if (!attemptId) {
      target.removeItem(AUTH_ATTEMPT_STORAGE_KEY);
      return;
    }
    const raw = target.getItem(AUTH_ATTEMPT_STORAGE_KEY);
    if (raw && (JSON.parse(raw) as Partial<PendingAuthAttempt>).id === attemptId) {
      target.removeItem(AUTH_ATTEMPT_STORAGE_KEY);
    }
  } catch {
    // Storage can be unavailable in private browsing; callback UI handles a missing marker.
  }
}
